import React, { useState } from 'react';
import { Dialog, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DosenButton from '../../../../ui/Buttons/DosenButton';

interface Props {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
}

export default function AddFileModal({ open, onClose, onUpload }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await onUpload(file);
      setFile(null);
      onClose();
    } catch (err) {
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 550
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
          Add files to your Program
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5
        }}
      >
        {!file && (
          <Box
            sx={{
              width: '100%',
              p: 5,
              borderRadius: 1.5,
              border: '1px solid #444444',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('fileInput')?.click()}
          >
            <InsertDriveFileIcon sx={{ fontSize: 48, color: '#FDFDFD' }} />
            <Typography
              sx={{
                color: '#B6B6B6',
                fontSize: 14,
                fontWeight: 400,
                textAlign: 'center'
              }}
            >
              Drag and drop your file
              <br />
              or click here or upload from your computer.
              <br />
              <br />
              File types supported:{' '}
              <Typography component="span" sx={{ color: '#B6B6B6', fontWeight: 700 }}>
                .png, .jpg, .pdf
              </Typography>
            </Typography>
            <input
              id="fileInput"
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {error && (
              <Typography sx={{ color: 'error.main', mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        )}

        {file && (
          <Box
            sx={{
              width: '100%',
              px: 3,
              py: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
              <InsertDriveFileIcon sx={{ fontSize: 24, color: '#868686' }} />
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 500, lineHeight: '14px' }}>
                {file.name}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: '5px',
                  background: 'var(--gray-700, #444444)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  sx={{
                    color: '#FDFDFD',
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    lineHeight: '14px'
                  }}
                >
                  {(file.name.split('.').pop() || '').toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <DosenButton
          onClick={handleUpload}
          disabled={!file || uploading}
          sx={{ minWidth: 200, position: 'relative' }}
        >
          {uploading && <CircularProgress size={18} sx={{ color: '#FDFDFD', position: 'absolute', left: 12 }} />}
          Add File
        </DosenButton>
      </Box>
    </Dialog>
  );
}
